import { Component, inject, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService } from '../../services/toast.service';

export type ToastPosition = 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="toast-container" [class.toast-container--top]="isTop()" [style]="containerStyle()">
      @for (t of toasts(); track t.id) {
        <div class="toast toast--{{ t.type }}" (click)="dismiss(t.id)">
          <span class="toast__icon">
            @if (t.type === 'success') { ✓ }
            @else if (t.type === 'error') { ✕ }
            @else { ℹ }
          </span>
          <span class="toast__message">{{ t.message }}</span>
        </div>
      }
    </div>
  `,
  styles: [`
    .toast-container {
      position: fixed;
      display: flex;
      flex-direction: column;
      gap: 10px;
      z-index: 9999;
      pointer-events: none;
      width: 90vw;
      max-width: 480px;
    }

    .toast {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 12px 20px;
      border-radius: 10px;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 4px 16px rgba(0,0,0,0.15);
      cursor: pointer;
      pointer-events: all;
      animation: toast-in-bottom 0.25s ease;
      white-space: normal;
      word-break: break-word;
      width: 100%;
      box-sizing: border-box;

      &--success { background: #1e293b; color: #fff; }
      &--error   { background: #dc2626; color: #fff; }
      &--info    { background: #0f172a; color: #fff; }
    }

    .toast-container--top .toast {
      animation: toast-in-top 0.25s ease;
    }

    .toast__icon { font-weight: 700; font-size: 15px; }

    @keyframes toast-in-bottom {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    @keyframes toast-in-top {
      from { opacity: 0; transform: translateY(-12px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `],
})
export class ToastContainerComponent {
  private readonly toastService = inject(ToastService);
  readonly toasts = this.toastService.toasts;
  readonly position = input<ToastPosition>('top');

  readonly isTop = computed(() => this.position().startsWith('top'));

  readonly containerStyle = computed((): Record<string, string> => {
    const pos = this.position();
    const centered = { left: '50%', transform: 'translateX(-50%)', alignItems: 'center' };
    switch (pos) {
      case 'top':          return { top: '24px', ...centered };
      case 'bottom':       return { bottom: '24px', ...centered };
      case 'top-left':     return { top: '24px', left: '24px', alignItems: 'flex-start' };
      case 'top-right':    return { top: '24px', right: '24px', alignItems: 'flex-end' };
      case 'bottom-left':  return { bottom: '24px', left: '24px', alignItems: 'flex-start' };
      case 'bottom-right': return { bottom: '24px', right: '24px', alignItems: 'flex-end' };
    }
  });

  dismiss(id: number): void { this.toastService.dismiss(id); }
}
